import { useEffect } from 'react';

import './typedSignature.css';

// HIPAA-friendly font loading: scope the Caveat Google Font request to the
// pages that actually need it (only the patient intake). Patients on /login,
// /dashboard, /schedule never hit Google. A ref-counter handles unmount/remount
// in StrictMode and ensures multiple TypedSignature instances on the same
// screen don't duplicate <link> tags.
let caveatMountCount = 0;
const NODE_IDS = {
  preconnect1: 'mmj-caveat-preconnect-1',
  preconnect2: 'mmj-caveat-preconnect-2',
  stylesheet: 'mmj-caveat-stylesheet',
};

const ensureNode = (id, tag, attrs) => {
  if (document.getElementById(id)) return;
  const el = document.createElement(tag);
  el.id = id;
  Object.keys(attrs).forEach((k) => {
    if (k === 'crossOrigin') {
      el.crossOrigin = attrs[k];
    } else {
      el.setAttribute(k, attrs[k]);
    }
  });
  document.head.appendChild(el);
};

const removeNode = (id) => {
  const el = document.getElementById(id);
  if (el && el.parentNode) el.parentNode.removeChild(el);
};

const loadCaveatFont = () => {
  caveatMountCount += 1;
  ensureNode(NODE_IDS.preconnect1, 'link', {
    rel: 'preconnect',
    href: 'https://fonts.googleapis.com',
  });
  ensureNode(NODE_IDS.preconnect2, 'link', {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  });
  ensureNode(NODE_IDS.stylesheet, 'link', {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Caveat:wght@500;700&display=swap',
  });
};

const unloadCaveatFont = () => {
  caveatMountCount = Math.max(0, caveatMountCount - 1);
  if (caveatMountCount === 0) {
    removeNode(NODE_IDS.stylesheet);
    removeNode(NODE_IDS.preconnect1);
    removeNode(NODE_IDS.preconnect2);
  }
};

const TypedSignature = ({
  value,
  onChange,
  attested,
  onAttestedChange,
  defaultName,
}) => {
  useEffect(() => {
    loadCaveatFont();
    return () => unloadCaveatFont();
  }, []);

  // Prefill from defaultName once if the field is empty.
  useEffect(() => {
    if (!value && defaultName && onChange) {
      onChange(defaultName);
    }
  }, []);

  return (
    <div className="TypedSignature flex flex-col space-y-4">
      <div>
        <label
          htmlFor="signed_name_input"
          className="block text-lg font-semibold text-mmj-header mb-2"
        >
          Type your full legal name to sign
        </label>
        <input
          id="signed_name_input"
          type="text"
          autoComplete="name"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="block w-full px-4 border border-gray-300 rounded-lg text-mmj-header focus:outline-none focus:ring-2 focus:ring-mmj-blue focus:border-mmj-blue"
          style={{ minHeight: '56px', fontSize: '22px' }}
          aria-required="true"
        />
        <p className="text-base text-gray-700 mt-2">
          Type your name the way it appears on your government-issued ID.
        </p>
      </div>

      <div>
        <div className="block text-lg font-semibold text-mmj-header mb-2">
          Your signature will look like this
        </div>
        <div
          className="border border-gray-300 rounded-lg bg-white flex items-center px-6 py-4"
          style={{ minHeight: '110px' }}
          aria-live="polite"
        >
          {value && value.trim().length > 0 ? (
            <span className="TypedSignature__preview text-mmj-header">
              {value}
            </span>
          ) : (
            <span className="text-gray-400 text-lg italic">
              Your signature will appear here
            </span>
          )}
        </div>
      </div>

      <label
        htmlFor="signed_attestation_checkbox"
        className="flex items-start space-x-3 cursor-pointer p-3 border border-gray-300 rounded-lg hover:border-mmj-blue"
        style={{ minHeight: '56px' }}
      >
        <input
          id="signed_attestation_checkbox"
          type="checkbox"
          checked={!!attested}
          onChange={(e) => onAttestedChange(e.target.checked)}
          className="mt-1 rounded border-gray-400 text-mmj-blue focus:ring-mmj-blue"
          style={{ width: '28px', height: '28px' }}
          aria-required="true"
        />
        <span className="text-base text-mmj-header leading-snug">
          I agree that the typed name above is my legal electronic signature on
          this intake form.
        </span>
      </label>
    </div>
  );
};

export default TypedSignature;
